import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { updateActivity } from "PFCore/services/activities";
import { ActivityData } from "PFTypes";

import { activityKeys } from "./query_keys";
import { useActivityInvalidate } from "./use_activity_invalidate";

const activityMutationsKeys = {
  update: (id: number) => [...activityKeys.activity(id), "update"]
};

export const useActivityUpdate = (id?: number) => {
  const cache = useQueryClient();
  const { invalidate: invalidateActivities } = useActivityInvalidate();

  const mutationKey = id ? activityMutationsKeys.update(id) : ["disabled-activity-update-mutation"];

  const updateMutation = useMutation({
    mutationFn: async (activityData: ActivityData = {}) => {
      const targetId = activityData.id ?? id;
      if (!targetId) {
        return;
      }

      await cache.cancelQueries({ queryKey: activityKeys.activity(targetId) });
      return updateActivity(targetId, activityData);
    },
    mutationKey,
    onSuccess: (_, activityData) => {
      const targetId = activityData?.id ?? id;
      if (!targetId) {
        return;
      }

      return Promise.all([
        cache.invalidateQueries({ queryKey: activityKeys.activity(targetId) }),
        invalidateActivities([targetId])
      ]);
    }
  });

  const isUpdating = useIsMutating({
    mutationKey
  });

  return {
    mutation: updateMutation,
    update: updateMutation.mutateAsync,
    isUpdating
  };
};
